import { io, Socket } from 'socket.io-client';
import env from '@/utils/env';

const SOCKET_URL = env.socketUrl;

interface ChatHandlers {
    onChatMessage: (message: any) => void;
    onChatStatusUpdate: (data: any) => void;
    onNewChat: (data: any) => void;

    onChatStaffJoined: (data: any) => void;
    onChatStaffLeft: (data: any) => void;
    onChatEnded: (data: any) => void;
    onChatTransferred: (data: any) => void;
    onStaffAvailabilityChanged?: (data: { userId: string; availableForChat: boolean }) => void;
    onStaffStatusUpdate?: (data: { userId: string; availableForChat: boolean }) => void;
    
}

interface SocketHandlers extends ChatHandlers {
    processNotification: (value: boolean) => void;
    setNotifications: (value: any[]) => void;
    setNewNotification: (value: boolean) => void;
    setOnlineUsers?: (users: Map<string, boolean>) => void;
    onTicketStatusChange?: (data: {
        event: string;
        data: any;
        timestamp: string;
    }) => void;
}

class SocketService {
    private static instance: SocketService;
    public socketInstance: Socket | null = null;
    private activityInterval: NodeJS.Timeout | null = null;
    private handlers: SocketHandlers | null = null;
    private userId: string | null = null;
    private guestEmail: string | null = null;
    private activeRooms: Set<string> = new Set();

    private constructor() {}

    static getInstance(): SocketService {
        if (!SocketService.instance) {
            SocketService.instance = new SocketService();
        }
        return SocketService.instance;
    }

    initialize(userId: string, accessToken: string, handlers: SocketHandlers): void {
        this.userId = userId;
        this.handlers = handlers;

        if (this.socketInstance?.connected) {
            this.reconnectToRooms();
            return;
        }

        this.socketInstance = io(SOCKET_URL, {
            transports: ['websocket', 'polling'],
            auth: {
                token: accessToken,
                userId: userId
            },
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            path: '/socket.io',
            query: { userId }
        });

        this.setupEventListeners();
        this.startActivityInterval();
    }

    initializeGuest(guestEmail: string, handlers: SocketHandlers): void {
        this.guestEmail = guestEmail;
        this.handlers = handlers;

        this.socketInstance = io(SOCKET_URL, {
            transports: ['websocket', 'polling'],
            auth: { guestEmail,
                type: 'guest'
             },
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            path: '/socket.io'
        });

        this.setupGuestEventListeners();
    }

    private reconnectToRooms(): void {
        this.activeRooms.forEach(room => {
            this.socketInstance?.emit('join-room', room);
        });
    }

    private setupEventListeners(): void {
        if (!this.socketInstance || !this.handlers) return;
    
        this.socketInstance.removeAllListeners();
    
        this.socketInstance.on('connect', () => {
            this.socketInstance?.emit('authenticate', { 
                userId: this.userId,
                rooms: Array.from(this.activeRooms)
            });
        });
    
        this.socketInstance.on('disconnect', () => {
            // setTimeout(() => {
            //     const isPageRefresh = window.location.href === document.referrer;
            //     if (!isPageRefresh && this.userId) {
            //         this.handlers?.setOnlineUsers?.(new Map([[this.userId, false]]));
            //     }
            // }, 5000);
            this.activeRooms.clear();
        });
    
        this.setupCommonEventListeners();
    }
    
    private setupGuestEventListeners(): void {
        if (!this.socketInstance || !this.handlers) return;

        this.socketInstance.removeAllListeners();

        this.socketInstance.on('connect', () => {
            this.socketInstance?.emit('authenticate-guest', { 
                guestEmail: this.guestEmail,
                rooms: Array.from(this.activeRooms)
            });
        });

        this.setupCommonEventListeners();
    }

    private setupCommonEventListeners(): void {
        if (!this.socketInstance || !this.handlers) return;

        this.socketInstance.on('staff.availability.changed', (data) => {
            this.handlers?.onStaffAvailabilityChanged?.(data);
          });
        
          this.socketInstance.on('staff.status.update', (data) => {
            this.handlers?.onStaffStatusUpdate?.(data);
          });

          this.socketInstance.on('chat.message', (data) => {
         //   console.log('Chat message received:', data);
            this.handlers?.onChatMessage(data);
        });
    
        this.socketInstance.on('chat-status', (data) => {
         //   console.log('Chat status update:', data);
            if (data.systemMessage) {
                this.handlers?.onChatMessage({
                    chat_room: data.chat_room,
                    message: {
                        content: {
                            message_type: 'system',
                            body: data.systemMessage
                        },
                        created_at: data.timestamp
                    },
                    timestamp: data.timestamp
                });
            }
            this.handlers?.onChatStatusUpdate(data);
        });
        this.socketInstance.on('new.chat', (data) => {
            this.handlers?.onNewChat(data);
        });

        this.socketInstance.on('chat.staff.joined', (data) => {
            this.handlers?.onChatStaffJoined(data);
        });

        this.socketInstance.on('chat.staff.left', (data) => {
            this.handlers?.onChatStaffLeft(data);
        });

        this.socketInstance.on('chat.ended', (data) => {
            this.handlers?.onChatEnded(data);
        });

        this.socketInstance.on('chat.transferred', (data) => {
            this.handlers?.onChatTransferred(data);
        });

        this.socketInstance.on('user-status-changed', (data) => {
            if (this.handlers?.setOnlineUsers) {
                this.handlers.setOnlineUsers(new Map([[data.userId, data.isOnline]]));
            }
            if (data.availableForChat !== undefined) {
                this.handlers?.onStaffAvailabilityChanged?.({
                  userId: data.userId,
                  availableForChat: data.availableForChat
                });
            }
        });

        this.socketInstance.on('ticket-status', (data) => {
            
            if (typeof data === 'string') {
                data = JSON.parse(data);
            }
            
            const { event, data: ticketData, timestamp } = data;
            this.handlers?.onTicketStatusChange?.({
                event,
                data: ticketData,
                timestamp
            });
        });
    }
    sendChatMessage(chatId: string, message: any): void {
        if (this.socketInstance?.connected) {
            this.socketInstance.emit('chat.message', {
                chatId,
                message,
                userId: this.userId
            });
        }
    }
    
    joinChat(chatId: string, userRole: string): void {
        if (this.socketInstance?.connected) {
            console.log('Joining chat:', chatId, userRole);
            this.socketInstance.emit('join-chat', chatId, userRole);
            this.activeRooms.add(`chat.${chatId}`);
            this.activeRooms.add(`user-${this.userId}`);
        }
    }
    
    joinGuestChat(chatId: string, guestEmail: string): void {
        if (this.socketInstance?.connected) {
            const roomId = `chat.${chatId}`;
            this.socketInstance.emit('join-guest-chat', { chatId, guestEmail });
            this.activeRooms.add(roomId);
            this.activeRooms.add(`chat.guest.${guestEmail}`);
        }
    }

    leaveChat(chatId: string): void {
        if (this.socketInstance?.connected) {
            const roomId = `chat.${chatId}`;
            this.socketInstance.emit('leave-chat', chatId);
            this.activeRooms.delete(roomId);
        }
    }


    private startActivityInterval(): void {
        if (this.activityInterval) {
            clearInterval(this.activityInterval);
        }

        this.activityInterval = setInterval(() => {
            if (this.socketInstance?.connected) {
                this.socketInstance.emit('user-activity', {
                    userId: this.userId,
                    timestamp: new Date().toISOString()
                });
            }
        }, 30000);
    }

    disconnect(): void {
        if (this.activityInterval) {
            clearInterval(this.activityInterval);
            this.activityInterval = null;
        }
    
        if (this.socketInstance?.connected) {
            this.socketInstance.emit('user-logout', {
                userId: this.userId,
                isOnline: false,
                lastActivity: new Date().toISOString()
            });
        }
    
        this.activeRooms.clear();
    
        if (this.socketInstance) {
            this.socketInstance.removeAllListeners();
            this.socketInstance.disconnect();
            this.socketInstance = null;
        }
    
        this.handlers = null;
        this.userId = null;
        this.guestEmail = null;
    }
}

export const socketService = SocketService.getInstance();
